import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { AlertTriangle, Badge, Cloud, ImageUp, Play, RefreshCw, Settings, Upload } from 'lucide-react';
import './styles.css';

type Api<T> = { ok: boolean; data?: T; error?: string };
type Tab = 'connections' | 'assets' | 'creatives' | 'presets' | 'jobs';

type AssetSummary = {
  connection: any | null;
  adAccounts: any[];
  pages: any[];
  datasets: any[];
};

type Creative = {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  primary_text?: string;
  headline?: string;
  destination_url?: string;
};

const objectives = [
  'OUTCOME_SALES',
  'OUTCOME_LEADS',
  'OUTCOME_TRAFFIC',
  'OUTCOME_ENGAGEMENT',
  'OUTCOME_AWARENESS',
  'OUTCOME_APP_PROMOTION'
];

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  const body = (await res.json()) as Api<T>;
  if (!body.ok) throw new Error(body.error || 'Request failed');
  return body.data as T;
}

function LegalPage({ type }: { type: 'terms' | 'privacy' }) {
  const isTerms = type === 'terms';
  return <main className="legalPage"><a className="button secondary back" href="/">Back to Submitty</a><h1>{isTerms ? 'Terms of Service' : 'Privacy Policy'}</h1><p>Last updated: June 25, 2026</p>{isTerms ? <TermsContent /> : <PrivacyContent />}<FooterLinks /></main>;
}

function ClientOAuthCallbackPage() {
  const [status, setStatus] = useState('Saving Facebook connection...');
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error_description') || params.get('error');
    if (error) { setStatus(`Meta OAuth error: ${error}`); return; }
    if (!code) { setStatus('Missing OAuth code. Please try connecting again.'); return; }
    fetch('/api/oauth/meta/exchange', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ code })
    }).then(async (res) => {
      const body = await res.json() as Api<any>;
      if (!body.ok) throw new Error(body.error || 'Could not save connection');
      setStatus('Facebook connected. You can go back to Submitty and sync assets.');
      window.history.replaceState({}, '', '/?connected=facebook');
    }).catch((err) => setStatus(`Could not save Facebook connection: ${err.message}`));
  }, []);
  return <main className="legalPage"><a className="button secondary back" href="/">Back to Submitty</a><h1>Facebook OAuth</h1><p>{status}</p><FooterLinks /></main>;
}

function ClientConnectPage() {
  const oauthUrl = new URL('https://www.facebook.com/v25.0/dialog/oauth');
  oauthUrl.searchParams.set('client_id', '2168133327064385');
  oauthUrl.searchParams.set('redirect_uri', 'https://submitty.net/api/oauth/meta/callback');
  oauthUrl.searchParams.set('state', crypto.randomUUID());
  oauthUrl.searchParams.set('scope', 'ads_management,ads_read,pages_show_list,pages_read_engagement');
  oauthUrl.searchParams.set('response_type', 'code');
  oauthUrl.searchParams.set('auth_type', 'rerequest');
  return <main className="legalPage"><a className="button secondary back" href="/">Back to Submitty</a><h1>Connect Facebook</h1><p>This fallback page is rendered by the browser. If the button does not open Meta, copy the URL below and paste it into your address bar.</p><p><a className="button" href={oauthUrl.toString()}>Continue to Facebook</a></p><textarea readOnly onClick={(e) => e.currentTarget.select()} value={oauthUrl.toString()} style={{ width: '100%', minHeight: 140, borderRadius: 12, padding: 12, background: '#020617', color: '#bfdbfe', border: '1px solid #263244' }} /><FooterLinks /></main>;
}

function TermsContent() {
  return <section><h2>1. Overview</h2><p>Submitty is a private advertising operations tool for connecting Meta accounts, managing creative assets, preparing campaign presets, and submitting advertising jobs.</p><h2>2. Authorized Use</h2><p>You may only use Submitty with advertising accounts, pages, datasets, creatives, and business assets that you own or are authorized to manage.</p><h2>3. Platform Compliance</h2><p>You are responsible for complying with Meta policies, advertising laws, platform rules, account restrictions, and applicable regulations. Submitty does not guarantee approval, delivery, spend, or performance of ads.</p><h2>4. Live Publishing</h2><p>Some workflows may publish campaigns, ad sets, or ads immediately. You are responsible for reviewing campaign settings, budgets, targeting, creative, and destination URLs before launch.</p><h2>5. No Warranty</h2><p>Submitty is provided as-is without warranties of any kind. We are not liable for rejected ads, account restrictions, budget spend, API errors, data loss, or business outcomes.</p><h2>6. Access</h2><p>Access is limited to authorized users. You are responsible for protecting login credentials and connected account permissions.</p><h2>7. Changes</h2><p>These terms may be updated as the product evolves. Continued use means acceptance of the latest terms.</p></section>;
}

function PrivacyContent() {
  return <section><h2>1. Data We Process</h2><p>Submitty may store account connection metadata, encrypted Meta access tokens, ad account/page/dataset identifiers, uploaded creative files, preset settings, and submission job logs.</p><h2>2. How Data Is Used</h2><p>Data is used to authenticate connected accounts, display available assets, upload creatives, create advertising objects, and show job history.</p><h2>3. Storage</h2><p>Application data is stored in Cloudflare D1. Creative files are stored in Cloudflare R2. Access tokens are encrypted before storage.</p><h2>4. Sharing</h2><p>Submitty sends data to Meta only as needed to authenticate, sync assets, upload creatives, and create ads. We do not sell personal data.</p><h2>5. Security</h2><p>Reasonable security controls are used, including encrypted token storage and authenticated admin access. No system can be guaranteed completely secure.</p><h2>6. Retention</h2><p>Data is retained while needed for operating the panel unless deleted by an authorized administrator.</p><h2>7. Contact</h2><p>For privacy questions, contact the site owner or administrator of this Submitty deployment.</p></section>;
}

function FooterLinks() {
  return <footer className="footerLinks"><span>© 2026 Submitty</span><a href="/terms">Terms</a><a href="/privacy">Privacy</a></footer>;
}

function App() {
  const [user, setUser] = useState<any | null | undefined>(undefined);
  const [password, setPassword] = useState('');
  const [tab, setTab] = useState<Tab>('connections');
  const [assets, setAssets] = useState<AssetSummary>({ connection: null, adAccounts: [], pages: [], datasets: [] });
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [message, setMessage] = useState('');
  const [dryRun, setDryRun] = useState<any | null>(null);
  const [config, setConfig] = useState({
    adAccountId: '',
    pageId: '',
    datasetId: '',
    creativeAssetId: '',
    campaignName: '',
    objective: 'OUTCOME_SALES',
    country: 'US',
    dailyBudgetCents: 101,
    adSetCount: 250,
    adsPerAdSet: 1,
    publishStatus: 'ACTIVE',
    adSetNamePattern: '{{campaignName}} - Ad Set {{index}}',
    adNamePattern: '{{campaignName}} - Ad {{index}}'
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const metaError = params.get('meta_error');
    const connected = params.get('connected');
    if (metaError) setMessage(`Meta OAuth error: ${metaError}`);
    if (connected) setMessage('Facebook connected. Click Sync assets next.');
    api<{ user: any | null }>('/api/auth/me').then((data) => setUser(data.user)).catch(() => setUser(null));
  }, []);

  async function refreshAll() {
    const [assetData, creativeData, jobData] = await Promise.all([
      api<AssetSummary>('/api/assets'),
      api<Creative[]>('/api/creatives'),
      api<any[]>('/api/jobs')
    ]);
    setAssets(assetData);
    setCreatives(creativeData);
    setJobs(jobData);
  }

  useEffect(() => {
    if (user) refreshAll().catch((err) => setMessage(err.message));
  }, [user]);

  async function login(e: React.FormEvent) {
    e.preventDefault();
    try {
      const data = await api<{ user: any }>('/api/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password })
      });
      setUser(data.user);
      setMessage('Logged in.');
    } catch (err: any) {
      setMessage(err.message);
    }
  }

  async function syncAssets() {
    setMessage('Syncing Meta assets...');
    await api('/api/assets/sync', { method: 'POST' });
    await refreshAll();
    setMessage('Assets synced.');
  }

  async function uploadCreative(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setMessage('Uploading creative...');
    await api('/api/creatives', { method: 'POST', body: form });
    e.currentTarget.reset();
    await refreshAll();
    setMessage('Creative uploaded.');
  }

  async function preview() {
    const data = await api('/api/jobs/dry-run', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(config)
    });
    setDryRun(data);
  }

  async function publish() {
    if (!confirm('This can publish live Meta ads. Are you sure you want to continue?')) return;
    const data = await api<any>('/api/jobs/publish', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(config)
    });
    setMessage(`Publish job created: ${data.jobId}. Status: ${data.status}`);
    await refreshAll();
  }

  const totalDaily = useMemo(() => (Number(config.dailyBudgetCents) * Number(config.adSetCount)) / 100, [config]);

  if (user === undefined) return <div className="center">Loading...</div>;
  if (!user) {
    return (
      <main className="loginShell">
        <form className="card login" onSubmit={login}>
          <Badge size={36} />
          <h1>Submitty</h1>
          <p>Admin login for the MVP. Employee users can be added later.</p>
          <input type="password" placeholder="Admin password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button>Login</button>
          {message && <p className="error">{message}</p>}
        </form>
      </main>
    );
  }

  return (
    <main className="app">
      <aside>
        <h1>Submitty</h1>
        <Nav active={tab} setTab={setTab} />
        <div className="asideFooter">Cloudflare Workers • D1 • R2<FooterLinks /></div>
      </aside>
      <section className="content">
        <header>
          <div>
            <h2>{tab[0].toUpperCase() + tab.slice(1)}</h2>
            <p>{message || 'Connect accounts, upload creatives, and submit Meta ads with presets.'}</p>
          </div>
          <button className="secondary" onClick={refreshAll}><RefreshCw size={16} /> Refresh</button>
        </header>

        {tab === 'connections' && <Connections assets={assets} onSync={syncAssets} />}
        {tab === 'assets' && <Assets assets={assets} />}
        {tab === 'creatives' && <Creatives creatives={creatives} onUpload={uploadCreative} />}
        {tab === 'presets' && (
          <Presets
            assets={assets}
            creatives={creatives}
            config={config}
            setConfig={setConfig}
            totalDaily={totalDaily}
            dryRun={dryRun}
            preview={preview}
            publish={publish}
          />
        )}
        {tab === 'jobs' && <Jobs jobs={jobs} />}
      </section>
    </main>
  );
}

function Nav({ active, setTab }: { active: Tab; setTab: (tab: Tab) => void }) {
  const items: [Tab, React.ReactNode][] = [
    ['connections', <Badge size={18} />],
    ['assets', <Cloud size={18} />],
    ['creatives', <ImageUp size={18} />],
    ['presets', <Settings size={18} />],
    ['jobs', <Play size={18} />]
  ];
  return <nav>{items.map(([item, icon]) => <button key={item} className={active === item ? 'active' : ''} onClick={() => setTab(item)}>{icon}{item}</button>)}</nav>;
}

function Connections({ assets, onSync }: { assets: AssetSummary; onSync: () => void }) {
  return <div className="grid two"><div className="card"><h3>Facebook OAuth</h3><p>{assets.connection ? `Connected as ${assets.connection.meta_user_name || assets.connection.meta_user_id}` : 'No Facebook connected yet.'}</p><a className="button" href="/connect-facebook">Connect Facebook</a><a className="secondary button" href="https://submitty.net/connect-facebook">Direct connect URL</a><button type="button" className="secondary" onClick={onSync}>Sync assets</button></div><div className="card"><h3>What this needs next</h3><ul><li>Meta app must allow the Submitty redirect URI</li><li>Use Direct connect URL if browser scripts/buttons act weird</li><li>After consent, return here and sync assets</li></ul><p className="muted">Direct URL: https://submitty.net/connect-facebook</p></div></div>;
}

function Assets({ assets }: { assets: AssetSummary }) {
  return <div className="grid three"><AssetList title="Ad Accounts" rows={assets.adAccounts} main="name" sub="account_id" /><AssetList title="Pages" rows={assets.pages} main="name" sub="page_id" /><AssetList title="Datasets / Pixels" rows={assets.datasets} main="name" sub="dataset_id" /></div>;
}

function AssetList({ title, rows, main, sub }: { title: string; rows: any[]; main: string; sub: string }) {
  return <div className="card"><h3>{title}</h3>{rows.length === 0 ? <p className="muted">None synced yet.</p> : rows.map((r) => <div className="row" key={r.id}><strong>{r[main] || r.id}</strong><span>{r[sub]}</span></div>)}</div>;
}
function Creatives({ creatives, onUpload }: { creatives: Creative[]; onUpload: (e: React.FormEvent<HTMLFormElement>) => void }) {
  return <div className="grid two"><form className="card" onSubmit={onUpload}><h3>Upload creative</h3><input name="file" type="file" accept="image/*,video/*" required /><input name="primaryText" placeholder="Primary text" /><input name="headline" placeholder="Headline" /><input name="description" placeholder="Description" /><input name="destinationUrl" placeholder="Destination URL" /><input name="tags" placeholder="Tags" /><button><Upload size={16} /> Upload to R2</button></form><div className="card"><h3>Library</h3>{creatives.length === 0 ? <p className="muted">No creatives uploaded yet.</p> : creatives.map((c) => <div className="row" key={c.id}><strong>{c.filename}</strong><span>{c.headline || c.content_type}</span></div>)}</div></div>;
}
function Presets({ assets, creatives, config, setConfig, totalDaily, dryRun, preview, publish }: any) {
  const set = (key: string, value: any) => setConfig((x: any) => ({ ...x, [key]: value }));
  return <div className="grid two wide"><div className="card"><h3>Preset: bill stern</h3><div className="warning"><AlertTriangle size={16} /> Default launches ACTIVE unless changed.</div><label>Ad account<select value={config.adAccountId} onChange={(e) => set('adAccountId', e.target.value)}><option value="">Select ad account</option>{assets.adAccounts.map((a: any) => <option key={a.account_id} value={a.account_id}>{a.name} ({a.account_id})</option>)}</select></label><label>Page<select value={config.pageId} onChange={(e) => set('pageId', e.target.value)}><option value="">Select page</option>{assets.pages.map((p: any) => <option key={p.page_id} value={p.page_id}>{p.name}</option>)}</select></label><label>Creative<select value={config.creativeAssetId} onChange={(e) => set('creativeAssetId', e.target.value)}><option value="">Select creative</option>{creatives.map((c: Creative) => <option key={c.id} value={c.id}>{c.filename}</option>)}</select></label><label>Campaign name<input value={config.campaignName} onChange={(e) => set('campaignName', e.target.value)} /></label><label>Objective<select value={config.objective} onChange={(e) => set('objective', e.target.value)}>{objectives.map((o) => <option key={o}>{o}</option>)}</select></label><label>Country<input value={config.country} onChange={(e) => set('country', e.target.value.toUpperCase())} /></label><div className="split"><label>Budget cents<input type="number" value={config.dailyBudgetCents} onChange={(e) => set('dailyBudgetCents', Number(e.target.value))} /></label><label>Ad sets<input type="number" value={config.adSetCount} onChange={(e) => set('adSetCount', Number(e.target.value))} /></label></div><label>Status<select value={config.publishStatus} onChange={(e) => set('publishStatus', e.target.value)}><option>ACTIVE</option><option>PAUSED</option></select></label><label>Ad set name pattern<input value={config.adSetNamePattern} onChange={(e) => set('adSetNamePattern', e.target.value)} /></label><label>Ad name pattern<input value={config.adNamePattern} onChange={(e) => set('adNamePattern', e.target.value)} /></label><p className="budget">Estimated daily budget: \${totalDaily.toFixed(2)}</p><button className="secondary" onClick={preview}>Dry-run preview</button><button className="danger" onClick={publish}>Publish live</button></div><div className="card"><h3>Dry-run preview</h3>{!dryRun ? <p className="muted">Generate a preview before publishing.</p> : <pre>{JSON.stringify(dryRun, null, 2)}</pre>}</div></div>;
}
function Jobs({ jobs }: { jobs: any[] }) {
  return <div className="card"><h3>Recent jobs</h3>{jobs.length === 0 ? <p className="muted">No jobs yet.</p> : jobs.map((j) => <div className="row" key={j.id}><strong>{j.status}</strong><span>{j.id} • {j.created_at}</span></div>)}</div>;
}
function Root() {
  const path = window.location.pathname.replace(/\/$/, '');
  if (path === '/api/oauth/meta/callback') return <ClientOAuthCallbackPage />;
  if (path === '/connect-facebook') return <ClientConnectPage />;
  if (path === '/terms') return <LegalPage type="terms" />;
  if (path === '/privacy') return <LegalPage type="privacy" />;
  return <App />;
}

createRoot(document.getElementById('root')!).render(<Root />);
